/**
 * Benachrichtigungen eines Servers bzw. Kanals (Vertrag 4.4): Stufe, stummschalten (mit Dauer),
 * @everyone/@here bzw. Rollen-Erwähnungen unterdrücken (nur Server). Zurücksetzen = DELETE (Standard).
 */
import type { NotificationLevel, NotificationSettingDto } from '@lagerfeuer/shared';
import { useState } from 'react';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { notificationsApi } from '../../lib/endpoints';
import { appState, useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import './notifications.css';

export type MuteDuration = '15m' | '1h' | '3h' | '8h' | '24h' | 'forever';
export const MUTE_LABELS: Record<MuteDuration, string> = {
  '15m': '15 Minuten',
  '1h': '1 Stunde',
  '3h': '3 Stunden',
  '8h': '8 Stunden',
  '24h': '24 Stunden',
  forever: 'Bis ich es wieder einschalte',
};
const MUTE_MS: Record<Exclude<MuteDuration, 'forever'>, number> = { '15m': 15 * 60_000, '1h': 3_600_000, '3h': 3 * 3_600_000, '8h': 8 * 3_600_000, '24h': 24 * 3_600_000 };

export function muteUntil(d: MuteDuration, now = Date.now()): string | null {
  return d === 'forever' ? null : new Date(now + MUTE_MS[d]).toISOString();
}

/** Aktiv stummgeschaltet (Ablauf berücksichtigt). */
export function isMutedSetting(s: Pick<NotificationSettingDto, 'muted' | 'mutedUntil'> | undefined, now = Date.now()): boolean {
  if (!s?.muted) return false;
  return !s.mutedUntil || new Date(s.mutedUntil).getTime() > now;
}

interface Props {
  serverId: string;
  channelId?: string;
  onClose: () => void;
}

export function NotificationSettingsDialog({ serverId, channelId, onClose }: Props) {
  const scope: 'SERVER' | 'CHANNEL' = channelId ? 'CHANNEL' : 'SERVER';
  const scopeId = channelId ?? serverId;
  const existing = useAppStore((s) => s.notificationSettings.find((x) => x.scopeType === scope && x.scopeId === scopeId));
  const serverSetting = useAppStore((s) => s.notificationSettings.find((x) => x.scopeType === 'SERVER' && x.scopeId === serverId));
  const server = useAppStore((s) => s.servers[serverId]);
  const channel = useAppStore((s) => (channelId ? s.channels[channelId] : undefined));

  const [level, setLevel] = useState<NotificationLevel>(existing?.level ?? 'DEFAULT');
  const [muted, setMuted] = useState(isMutedSetting(existing));
  const canKeep = isMutedSetting(existing) && !!existing?.mutedUntil;
  const [duration, setDuration] = useState<MuteDuration | 'keep'>(canKeep ? 'keep' : 'forever');
  const [suppressEveryone, setSuppressEveryone] = useState(existing?.suppressEveryone ?? false);
  const [suppressRoles, setSuppressRoles] = useState(existing?.suppressRoles ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serverDefault = server?.defaultNotifications === 'ALL' ? 'Alle Nachrichten' : 'Nur @Erwähnungen';
  const inherited =
    scope === 'CHANNEL'
      ? `Wie Server (${serverSetting && serverSetting.level !== 'DEFAULT' ? LEVEL_LABEL[serverSetting.level] : serverDefault})`
      : `Serverstandard (${serverDefault})`;
  const levels: { id: NotificationLevel; label: string }[] = [
    { id: 'DEFAULT', label: inherited },
    { id: 'ALL', label: LEVEL_LABEL.ALL },
    { id: 'MENTIONS', label: LEVEL_LABEL.MENTIONS },
    { id: 'NOTHING', label: LEVEL_LABEL.NOTHING },
  ];

  const apply = (res: NotificationSettingDto | null) =>
    appState().update((d) => {
      d.notificationSettings = d.notificationSettings.filter((x) => !(x.scopeType === scope && x.scopeId === scopeId));
      if (res) d.notificationSettings.push(res);
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    // Unveränderte, befristete Stummschaltung behält ihr Ende.
    const mutedUntil = muted ? (duration === 'keep' ? (existing?.mutedUntil ?? null) : muteUntil(duration)) : null;
    try {
      const res =
        scope === 'SERVER'
          ? await notificationsApi.setServer(serverId, { level, muted, mutedUntil, suppressEveryone, suppressRoles })
          : await notificationsApi.setChannel(scopeId, { level, muted, mutedUntil });
      apply(res);
      toast.success('Benachrichtigungen gespeichert');
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      if (scope === 'SERVER') await notificationsApi.resetServer(serverId);
      else await notificationsApi.resetChannel(scopeId);
      apply(null);
      toast.success('Auf Standard zurückgesetzt');
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const title = scope === 'SERVER' ? `Benachrichtigungen – ${server?.name ?? 'Server'}` : `Benachrichtigungen – #${channel?.name ?? 'Kanal'}`;

  return (
    <Modal
      title={title}
      onClose={onClose}
      testId="notification-settings"
      footer={
        <>
          {existing && (
            <button type="button" className="btn btn-ghost" onClick={() => void reset()} disabled={busy} data-testid="notif-reset">
              Zurücksetzen
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="notif-cancel">
            Abbrechen
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy} data-testid="notif-save">
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="lf-notif-mute">
          <label className="switch-row">
            <input type="checkbox" className="switch" checked={muted} onChange={(e) => setMuted(e.target.checked)} data-testid="notif-mute" />
            <span>{scope === 'SERVER' ? 'Server stummschalten' : 'Kanal stummschalten'}</span>
          </label>
          {muted && (
            <div className="field">
              <label htmlFor="notif-mute-duration">Dauer</label>
              <select id="notif-mute-duration" className="select" value={duration} onChange={(e) => setDuration(e.target.value as MuteDuration | 'keep')} data-testid="notif-mute-duration">
                {canKeep && existing?.mutedUntil && (
                  <option value="keep">Unverändert (bis {new Date(existing.mutedUntil).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })})</option>
                )}
                {(Object.keys(MUTE_LABELS) as MuteDuration[]).map((k) => (
                  <option key={k} value={k}>
                    {MUTE_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <p className="field-hint">Stummgeschaltet: keine Hinweise und keine Ungelesen-Markierung – außer bei Erwähnungen, wenn die Stufe sie zulässt.</p>
        </div>
        <fieldset className="lf-fieldset">
          <legend className="label">Benachrichtigen bei</legend>
          <div role="radiogroup" className="lf-notif-levels">
            {levels.map((l) => (
              <label key={l.id} className="lf-notif-level" data-checked={level === l.id || undefined}>
                <input type="radio" name="notif-level" checked={level === l.id} onChange={() => setLevel(l.id)} data-testid={`notif-level-${l.id}`} />
                <span>{l.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {scope === 'SERVER' && (
          <div className="stack" style={{ gap: '0.5rem' }}>
            <label className="switch-row">
              <input type="checkbox" className="switch" checked={suppressEveryone} onChange={(e) => setSuppressEveryone(e.target.checked)} data-testid="notif-suppress-everyone" />
              <span>@everyone und @here unterdrücken</span>
            </label>
            <label className="switch-row">
              <input type="checkbox" className="switch" checked={suppressRoles} onChange={(e) => setSuppressRoles(e.target.checked)} data-testid="notif-suppress-roles" />
              <span>Alle Rollen-Erwähnungen unterdrücken</span>
            </label>
          </div>
        )}
        {error && (
          <p className="error-text" role="alert" data-testid="notif-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

const LEVEL_LABEL: Record<Exclude<NotificationLevel, 'DEFAULT'>, string> = {
  ALL: 'Alle Nachrichten',
  MENTIONS: 'Nur @Erwähnungen',
  NOTHING: 'Nichts',
};
